import dayjs from 'dayjs';
import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import type { GridColDef, SortingObservable } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import { GLOBALS_PATH, PRACTICE_PATH } from '../../store/constants';
import {
  PracticeConfig,
  PracticeReport,
  type CsVersion,
  type CsVersionValidation,
} from '../../types/practice-types';
import { isDefined, sortByField } from '../../utils/object-utils';
import { regexToSearchWithWildcards } from '../../utils/search-utils';
import { semverLessThan } from '../../utils/semver-utils';
import {
  ActionsRenderer,
  ContactsRenderer,
  CsVersionRenderer,
  dateRenderer,
  MessageCancelUpgrade,
  MessageCancelUpgradeCommunicationError,
  MessageCancelUpgradeError,
  MessageConfirmDownloadFilesPresent,
  ReportLinkRenderer,
  SapIdRenderer,
  StatusRenderer,
} from './renderers';
import UpgradeReportsModalContent from './UpgradeReportsModalContent';

interface Loaders {
  save?: boolean;
  page?: boolean;
  refresh?: boolean;
  maxUpgrades?: boolean;
}

const MAX_UPGRADE_LENGTH = 7;

export default class UpgradeManagementStore extends AbstractStore {
  @observable loaders: Loaders = {};

  @observable
  public items: PracticeConfig[] = [];

  @observable
  public csVersions: CsVersion[] = [];

  @observable
  public operatingItemIndex: number | undefined = undefined;

  @observable
  public maxUpgrades = '0';

  @observable
  public maxUpgradesEditMode = false;

  @observable
  public maxUpgradesCurrent = '0';

  @observable
  public maxUpgradesError = '';

  @observable
  public sorting: SortingObservable<PracticeConfig> = {};

  @observable
  public futureOnly = false;

  @observable
  public searchText = '';

  @observable
  public editItemIndex = -1;

  @observable
  public isNewItem = false;

  @observable
  public currentCsVersionIndex = -1;

  @computed
  public get colDefs(): GridColDef<PracticeConfig>[] {
    return [
      {
        headerName: 'Upgrade Status',
        field: 'scheduleUpgradeStatus',
        renderer: StatusRenderer,
        sortable: true,
      },
      { headerName: 'SAP ID', field: 'sapId', renderer: SapIdRenderer, sortable: true },
      { headerName: 'Practice Name', field: 'practiceName', sortable: true },
      {
        headerName: 'CS Version',
        field: 'toCsVersion.version',
        sortable: true,
        renderer: CsVersionRenderer,
      },
      {
        headerName: 'Upgrade Date And Time',
        field: 'scheduleUpgradeTimestamp',
        renderValue: props => dateRenderer(props.row.scheduleUpgradeTimestamp),
        sortable: true,
      },
      {
        headerName: 'Machine Count',
        field: '',
        renderValue: props => String(props.row.successAgentIds?.length || ''),
        sortable: false,
      },
      {
        headerName: 'Practice Contacts',
        field: 'contacts',
        renderer: ContactsRenderer,
        sortable: false,
      },
      { headerName: 'Last Updated By', field: 'scheduledBy', sortable: true },
      {
        headerName: 'Last Updated On',
        field: 'scheduleTimestamp',
        renderValue: props => dateRenderer(props.row.scheduleTimestamp),
        sortable: true,
      },
      { headerName: 'Actions', field: '', renderer: ActionsRenderer, sortable: false },
    ];
  }

  @computed
  public get reportsColDefs(): GridColDef<PracticeReport>[] {
    return [
      { headerName: 'Report Name', field: 'path', renderer: ReportLinkRenderer },
      {
        headerName: 'Created Date',
        field: 'timestamp',
        renderValue: props => dateRenderer(props.row.timestamp),
      },
    ];
  }

  @computed
  public get itemsFiltered(): PracticeConfig[] {
    let filtered = [...this.items];
    let editItem: PracticeConfig | undefined = undefined;
    if (this.isNewItem && this.editItemIndex > -1) {
      editItem = filtered[this.editItemIndex];
      filtered.splice(this.editItemIndex, 1);
    }
    if (this.searchText !== '') {
      const regex = regexToSearchWithWildcards(this.searchText);
      filtered = this.items.filter(
        i =>
          regex.test(i.sapId || '') ||
          regex.test(i.practiceName || '') ||
          regex.test(i.scheduledBy || '') ||
          i.contacts?.some(c => regex.test(c.name)),
      );
    }
    if (this.futureOnly) {
      const now = dayjs().valueOf();
      filtered = filtered.filter(
        i => isDefined(i.scheduleUpgradeTimestamp) && i.scheduleUpgradeTimestamp > now,
      );
    }
    if (this.sorting.field) {
      if (this.sorting.field === 'toCsVersion.version') {
        filtered = sortByField(filtered, 'toCsVersion.versionForSorting');
      } else {
        filtered = sortByField(filtered, this.sorting.field);
      }
      if (this.sorting.order === 'DESC') {
        filtered = filtered.reverse();
      }
    }
    return editItem ? [editItem, ...filtered] : filtered;
  }

  @computed
  public get operationInProgress(): boolean {
    return this.operatingItemIndex !== undefined;
  }

  @action
  public async init(): Promise<void> {
    runInAction(() => {
      this.loaders = { page: true };
      this.sorting = { field: 'sapId', order: 'ASC' };
      this.futureOnly = false;
      this.searchText = '';
      this.operatingItemIndex = undefined;
      this.maxUpgradesEditMode = false;
      this.editItemIndex = -1;
      this.isNewItem = false;
    });
    const items = await this.root.apiStore.getAllPractices();
    const maxUpdatesPerDay = await this.root.apiStore.getGlobalValue<number>(
      'UPGRADES_PER_DAY_MAXIMUM_NUMBER',
    );
    const csVersions = await this.root.apiStore.getCsVersions();
    runInAction(() => {
      this.items = items;
      this.csVersions = csVersions.sort((v1, v2) => {
        if (v1.default) return -1;
        if (v2.default) return 1;
        return semverLessThan(v1.version, v2.version) ? 1 : -1;
      });
      this.maxUpgrades = String(maxUpdatesPerDay || 0);
      this.loaders.page = false;
    });
  }

  @action
  public async refresh(): Promise<void> {
    runInAction(() => {
      this.loaders.refresh = true;
      this.items = [];
    });
    const items = await this.root.apiStore.getAllPractices();
    runInAction(() => {
      this.items = items;
      this.loaders.refresh = false;
    });
  }

  @action
  public promptCancelUpgrade(index: number): void {
    this.root.globalStore.showOkCancelPopup(
      'Cancel Upgrade',
      React.createElement(MessageCancelUpgrade),
      'Confirm',
      'Cancel',
      () => this.cancelUpgrade(index),
      undefined,
      false,
      false,
    );
  }

  @action
  public startEditingMaxUpgrades(): void {
    this.maxUpgradesCurrent = this.maxUpgrades;
    this.maxUpgradesEditMode = true;
    this.maxUpgradesError = '';
  }

  @action
  public cancelEditingMaxUpgrades(): void {
    this.maxUpgradesEditMode = false;
    this.maxUpgradesError = '';
  }

  @action
  public changeMaxUpgrades(value: string): void {
    if (/^\d*$/.test(value) && value.length <= MAX_UPGRADE_LENGTH) this.maxUpgradesCurrent = value;
  }

  @action
  public async updateMaxUpgrades(): Promise<void> {
    const maxUpgradesNumber = parseInt(this.maxUpgradesCurrent);
    if (maxUpgradesNumber === 0) {
      runInAction(() => (this.maxUpgradesError = 'Cannot be 0'));
    } else if (maxUpgradesNumber === parseInt(this.maxUpgrades)) {
      runInAction(() => (this.maxUpgradesEditMode = false));
    } else {
      runInAction(() => (this.loaders.maxUpgrades = true));
      await this.root.apiStore.backendPost(GLOBALS_PATH, {
        key: 'UPGRADES_PER_DAY_MAXIMUM_NUMBER',
        value: maxUpgradesNumber,
      });
      runInAction(() => {
        this.maxUpgrades = this.maxUpgradesCurrent;
        this.maxUpgradesEditMode = false;
        this.loaders.maxUpgrades = false;
      });
    }
  }

  @action
  public addItem(): void {
    this.currentCsVersionIndex = this.csVersions.findIndex(v => v.default);
    this.items = [
      { sapId: '', practiceName: '', toCsVersion: this.csVersions[this.currentCsVersionIndex] },
      ...this.items,
    ];
    this.editItemIndex = 0;
    this.isNewItem = true;
  }

  @action
  public startEdit(index: number): void {
    this.editItemIndex = index;
    const item = this.items.find(i => i.sapId === this.itemsFiltered[index].sapId);
    this.currentCsVersionIndex = this.csVersions.findIndex(
      v => v.version === item?.toCsVersion.version,
    );
  }

  @action
  public async saveItem(): Promise<void> {
    runInAction(() => {
      this.loaders.save = true;
    });
    try {
      const csVersionItem = this.csVersions[this.currentCsVersionIndex];
      await this.root.apiStore.backendPost<CsVersionValidation>(PRACTICE_PATH, {
        action: this.isNewItem ? 'CREATE' : 'UPDATE_CS_VERSION',
        sapId: this.itemsFiltered[this.editItemIndex].sapId,
        csVersion: csVersionItem.version,
      });
      if (this.isNewItem) {
        await this.root.apiStore.fetchCurrentUserDetails();
      }
      runInAction(() => {
        const item = this.items.find(i => i.sapId === this.itemsFiltered[this.editItemIndex].sapId);
        item!.toCsVersion = csVersionItem;
        this.isNewItem = false;
        this.editItemIndex = -1;
      });
    } catch (e: any) {
      if (e.message?.includes('already exists')) {
        this.root.globalStore.showOkPopup(
          'Record Exists For Practice',
          'This practice has upgraded with MyCornerstone before. Please edit the existing record.',
        );
      }
    } finally {
      runInAction(() => {
        this.loaders.save = false;
      });
    }
  }

  @action
  public cancelEditing(): void {
    if (this.isNewItem) {
      this.items.splice(0, 1);
    }
    this.isNewItem = false;
    this.editItemIndex = -1;
  }

  @action
  public async markFailedDownloadsAsComplete(index: number): Promise<void> {
    runInAction(() => {
      this.root.globalStore.globalModalProps.show = false;
      this.operatingItemIndex = index;
    });
    try {
      const practice = await this.root.apiStore.backendPost<PracticeConfig>(PRACTICE_PATH, {
        action: 'MARK_FAILED_DOWNLOADS_AS_COMPLETE',
        sapId: this.itemsFiltered[index].sapId,
      });
      runInAction(() => {
        const itemIndex = this.items.findIndex(i => i.sapId === this.itemsFiltered[index].sapId);
        if (itemIndex > -1) {
          this.items[itemIndex] = practice;
        }
      });
    } catch {
      this.root.globalStore.showOkPopup(
        'Connection error',
        'Unable to perform operation, try again later',
      );
    }
    runInAction(() => (this.operatingItemIndex = undefined));
  }

  @action
  public showUpgradeReports(index: number): void {
    const item = this.itemsFiltered[index];
    const reports = sortByField(item.reports || [], 'timestamp').reverse();
    this.root.globalStore.showOkPopup(
      `Upgrade Reports for ${item.sapId}`,
      React.createElement(UpgradeReportsModalContent, { reports }),
      () => {},
      'Close',
    );
  }

  @action
  public async downloadQualificationPdf(index: number): Promise<void> {
    runInAction(() => (this.loaders.refresh = true));
    const item = this.itemsFiltered[index];
    try {
      const pdfBlob = await this.root.apiStore.getAgentsQualificationsPdf(item.sapId);
      window.open(URL.createObjectURL(pdfBlob), '_blank');
    } catch (e: any) {
      console.log(e);
      this.root.globalStore.showOkPopup('Not available', 'No hardware data available to download');
    } finally {
      runInAction(() => (this.loaders.refresh = false));
    }
  }

  @action
  private async cancelUpgrade(index: number): Promise<void> {
    runInAction(() => {
      this.root.globalStore.globalModalProps.show = false;
      this.operatingItemIndex = index;
    });
    void this.audit('UPGRADE_MANAGEMENT', 'UPGRADE_MANAGEMENT_PAGE_CANCEL_UPGRADE');
    try {
      const practice = await this.root.apiStore.backendPost(PRACTICE_PATH, {
        action: 'CANCEL_UPGRADE',
        sapId: this.itemsFiltered[index].sapId,
      });
      runInAction(() => {
        const itemIndex = this.items.findIndex(i => i.sapId === this.itemsFiltered[index].sapId);
        if (itemIndex > -1) {
          this.items[itemIndex] = practice;
        }
      });
    } catch {
      this.root.globalStore.showOkPopup(
        'Connection error',
        'Unable to connect to Kaseya, please try again later',
      );
    }
    if (this.itemsFiltered[index].scheduleError === 'Kaseya') {
      this.root.globalStore.showOkPopup(
        'Error Cancelling',
        React.createElement(MessageCancelUpgradeError),
      );
    }
    if (this.itemsFiltered[index].scheduleError === 'Communication') {
      this.root.globalStore.showOkPopup(
        'Communication error',
        React.createElement(MessageCancelUpgradeCommunicationError),
      );
    }
    runInAction(() => (this.operatingItemIndex = undefined));
  }

  public promptFailedDownloadsAsComplete(index: number): void {
    this.root.globalStore.showOkCancelPopup(
      'Confirm Install Files Present',
      React.createElement(MessageConfirmDownloadFilesPresent, {
        agentsList: this.itemsFiltered[index].upgradeDisplayState?.details,
      }),
      'Confirm',
      'Cancel',
      () => this.markFailedDownloadsAsComplete(index),
      undefined,
      false,
      false,
    );
  }

  public isMarkFailedDownloadsAsCompleteEnabled(item: PracticeConfig): boolean {
    return (
      item.upgradeDisplayState?.type === 'warning' &&
      item.upgradeDisplayState.title.toLowerCase().includes('download')
    );
  }
}
